import { createContext, ReactNode, useContext, useEffect, useMemo, useState } from 'react';

export type Theme = 'mflow-dark' | 'mflow-light';

const STORAGE_KEY = 'mflow_theme';
const THEME_COLOR: Record<Theme, string> = { 'mflow-light': '#f3efe6', 'mflow-dark': '#0b0d10' };

interface ThemeContextValue {
  theme: Theme;
  setTheme: (theme: Theme) => void;
  toggleTheme: () => void;
}

const ThemeContext = createContext<ThemeContextValue | undefined>(undefined);

// The no-FOUC inline script in public/index.html already stamps data-theme on <html>
// before first paint — read it back so React's initial render matches, instead of
// defaulting to a value that might cause a flash on mount.
function readInitialTheme(): Theme {
  const attr = document.documentElement.getAttribute('data-theme');
  return attr === 'mflow-light' ? 'mflow-light' : 'mflow-dark';
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setThemeState] = useState<Theme>(readInitialTheme);

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    document.documentElement.style.colorScheme = theme === 'mflow-light' ? 'light' : 'dark';
    document.querySelector('meta[name="theme-color"]')?.setAttribute('content', THEME_COLOR[theme]);
  }, [theme]);

  useEffect(() => {
    if (localStorage.getItem(STORAGE_KEY)) return; // explicit user choice already made — don't override it
    const mql = window.matchMedia('(prefers-color-scheme: light)');
    const onChange = (e: MediaQueryListEvent) => {
      if (localStorage.getItem(STORAGE_KEY)) return;
      setThemeState(e.matches ? 'mflow-light' : 'mflow-dark');
    };
    mql.addEventListener('change', onChange);
    return () => mql.removeEventListener('change', onChange);
  }, []);

  const value = useMemo<ThemeContextValue>(
    () => ({
      theme,
      setTheme: (next) => {
        localStorage.setItem(STORAGE_KEY, next);
        setThemeState(next);
      },
      toggleTheme: () => {
        const next: Theme = theme === 'mflow-dark' ? 'mflow-light' : 'mflow-dark';
        localStorage.setItem(STORAGE_KEY, next);
        setThemeState(next);
      },
    }),
    [theme],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used within ThemeProvider');
  return ctx;
}
